(function () {

    const section = document.querySelector(`[id="product"]`);
    const product = JSON.parse(section.querySelector(".productJSON").textContent);

    // slide order used to clear later answers when an earlier one changes
    const optionGroups = ["gender", "sport", "size", "weight", "age"];

    // possible gender/age values come from the rendered labels so Liquid stays the source of truth
    const genderValues = Array.from(section.querySelectorAll(`.productFormOptionSlideLabel[data-group="gender"]`)).map(element => element.dataset.value);
    const ageValues = Array.from(section.querySelectorAll(`.productFormOptionSlideLabel[data-group="age"]`)).map(element => element.dataset.value);
    // top cover is based off of sport (GOLD INSOLES)
    const sportToTopCover = {
        "Baseball/Softball": "B",
        "Basketball": "A",
        "Ultimate Frisbee": "B",
        "Boxing/Wrestling": "B",
        "Cheerleading": "C",
        "Fitness": "A",
        "Golf": "B",
        "Hiking": "A",
        "Football": "B",
        "Lacrosse": "B",
        "Rugby": "B",
        "Running": "A",
        "Soccer": "C",
        "Tennis/Pickleball": "A",
        "Track & Field": "C",
        "Volleyball": "A",
        "Walking": "A",
        "Other": "A",
    }

    /**
     * Check if a partial selection can still end at a variant we carry, sold out or not.
     * Missing answers are wildcards: they are enumerated over every value they could still take.
     * @param {{gender?: string, sport?: string, size?: string, weight?: string, age?: string}} options partial selection
     * @returns {boolean} true when at least one completion of the missing answers is a variant we carry
     */
    function canComplete(options) {
        // assume possible on templates that do not render the gold product globals
        if (typeof goldWeightToProduct === "undefined") {
            return true;
        }

        // unknown answers are enumerated over every value they could still take
        const genders = options.gender ? [options.gender] : genderValues;
        const weights = options.weight ? [options.weight] : Object.keys(goldWeightToProduct);
        const ages = options.age ? [options.age] : ageValues;
        const topCover = options.sport ? sportToTopCover[options.sport] : null;

        // possible iff any routing triple resolves to a product that carries a matching variant
        return genders.some(gender => weights.some(weight => ages.some(age =>
            hasCarriedVariant(resolveTargetProduct(weight, age, gender), {size: options.size ?? null, topCover, gender})
        )));
    }

    /**
     * Check if a product carries any variant matching the (possibly partial) filters.
     * Stock is deliberately ignored: a sold out variant is still one we carry, so it must not
     * gray out the choice that leads to it.
     * @param {Object} targetProduct Shopify product JSON
     * @param {{size: ?string, topCover: ?string, gender: ?string}} filters values to match, null matches anything
     * @returns {boolean} true when at least one matching variant exists, in stock or not
     */
    function hasCarriedVariant(targetProduct, filters) {
        return targetProduct.variants.some(variant => variantMatches(variant, filters));
    }

    /**
     * Resolve which pro level product a weight/age/gender combination routes to.
     * @param {string} weight weight bracket key of goldWeightToProduct, e.g. "151 - 180lbs"
     * @param {string} age age bracket, e.g. "21 - 30" ("50 and Up" drops one pro level)
     * @param {string} gender "Male" | "Female" (Female at 151-180lbs steps down to L3)
     * @returns {Object} the pro level product JSON (goldProductL1/L3/L4/L5/L6)
     */
    function resolveTargetProduct(weight, age, gender) {
        // base product comes from the weight bracket
        let targetProduct = goldWeightToProduct[weight];

        // Note we do not touch the weight variable since it's used as a line item property
        // if they are Female and size 151-180, it changes the weight to pro level 3 from level 4 (we have another case for ages 50 and up so we exclude that)
        if (gender === "Female" && weight === "151 - 180lbs" && age !== "50 and Up") {
            targetProduct = goldWeightToProduct["121 - 150lbs"];
        }

        // if they are age 50 & up, we decrease the pro level by 1
        if (age === "50 and Up") {
            if (weight === "261lbs - Up") {
                targetProduct = goldWeightToProduct["231 - 260lbs"];
            } else if (weight === "231 - 260lbs" || weight === "211 - 230lbs") {
                targetProduct = goldWeightToProduct["181 - 210lbs"];
            } else if (weight === "181 - 210lbs" || weight === "151 - 180lbs") {
                targetProduct = goldWeightToProduct["121 - 150lbs"];
            } else if (weight === "121 - 150lbs") {
                targetProduct = goldWeightToProduct["100 - 120lbs"];
            } else {
                targetProduct = goldWeightToProduct["100 - 120lbs"];
            }
        }

        return targetProduct;
    }

    /**
     * Check a variant against partial filters, null fields act as wildcards.
     * @param {Object} variant Shopify variant JSON (option1 size range, option2 top cover, option3 gender)
     * @param {{size: ?string, topCover: ?string, gender: ?string}} filters values to match, null matches anything
     * @returns {boolean} true when the variant satisfies every non-null filter
     */
    function variantMatches(variant, filters) {
        // size ranges like "5-5.5" are split so the exact size can be matched
        if (filters.size !== null && !variant.option1.split("-").includes(filters.size)) {
            return false;
        }

        // top cover letter
        if (filters.topCover !== null && variant.option2 !== filters.topCover) {
            return false;
        }

        // gender
        if (filters.gender !== null && variant.option3 !== filters.gender) {
            return false;
        }

        return true;
    }

    /**
     * Read the currently selected wizard answers into a partial options object.
     * @returns {Object} answered groups only, e.g. {gender: "Female", sport: "Basketball"}
     */
    function getSelectedOptions() {
        let options = {};
        section.querySelectorAll(".productFormOptionSlideLabel.active").forEach(element => {
            options[element.dataset.group] = element.dataset.value;
        });
        return options;
    }

    /**
     * Gray out every wizard choice that can only lead to variants we do not carry.
     * Sold out variants are still carried, so they never gray a choice out.
     * Clicking a label clears every later answer, so each label is constrained only by
     * answers on slides before it, and the first slide is never grayed.
     */
    function refreshDisabledOptions() {
        const selected = getSelectedOptions();

        section.querySelectorAll(".productFormOptionSlideLabel").forEach(element => {
            const group = element.dataset.group;

            // the first slide always stays fully clickable
            if (group === optionGroups[0]) {
                element.classList.remove("soldOut");
                return;
            }

            // clicking a label clears every later answer, so only earlier answers constrain it
            let options = {[group]: element.dataset.value};
            optionGroups.slice(0, optionGroups.indexOf(group)).forEach(earlierGroup => {
                if (selected[earlierGroup]) {
                    options[earlierGroup] = selected[earlierGroup];
                }
            });

            element.classList.toggle("soldOut", !canComplete(options));
        });
    }

    /**
     * Disable dead-end choices inside a pair modal and reset selects that just became invalid.
     * Mirrors refreshDisabledOptions: each select is constrained only by the selects before it
     * in the wizard order, and later selects whose value became invalid reset to the placeholder.
     * @param {Element} pairElement the .productFormPair containing the modal to refresh
     */
    function refreshPairModalDisabledOptions(pairElement) {
        const selects = pairElement.querySelectorAll(".productFormPairModalLabelSelect");

        // read the current answers, treating the "Choose ..." placeholder as unanswered
        let current = {};
        selects.forEach(select => {
            if (select.selectedIndex > 0) {
                current[select.getAttribute("name")] = select.value;
            }
        });

        // disable every insole choice that can no longer end at a variant we carry
        pairElement.querySelectorAll(".productFormPairModalLabelSelect.insole").forEach(select => {
            const name = select.getAttribute("name");

            // invalid later answers auto-reset below, so only earlier answers constrain a select
            let earlierOptions = {};
            optionGroups.slice(0, optionGroups.indexOf(name)).forEach(earlierGroup => {
                if (current[earlierGroup]) {
                    earlierOptions[earlierGroup] = current[earlierGroup];
                }
            });

            select.querySelectorAll("option").forEach((option, index) => {
                if (index === 0) {
                    return;
                }
                option.disabled = !canComplete({...earlierOptions, [name]: option.value});
            });
        });

        // disable footwear sizes that are out of stock for the chosen footwear, color and gender
        refreshPairModalFootwearSizes(pairElement);

        // reset any select whose chosen value just became disabled, then re-check with the widened answers
        let resetNeeded = false;
        selects.forEach(select => {
            if (select.selectedIndex > 0 && select.options[select.selectedIndex].disabled) {
                select.selectedIndex = 0;
                resetNeeded = true;
            }
        });
        if (resetNeeded) {
            refreshPairModalDisabledOptions(pairElement);
            return;
        }

        // keep the update button in sync since programmatic resets do not fire change events
        let ready = true;
        selects.forEach(select => {
            if (select.selectedIndex === 0) {
                ready = false;
            }
        });
        pairElement.querySelector(".productFormPairModalUpdate").disabled = !ready;
    }

    /**
     * Check if a footwear product has an in stock variant for the given options.
     * @param {Object} footwearProduct Shopify product JSON for the clogs or slides
     * @param {string[]} footwearOptions [color, gender, size] in the product's option order
     * @returns {boolean} true when a matching variant exists and is available
     */
    function isFootwearAvailable(footwearProduct, footwearOptions) {
        return footwearProduct.variants.some(variant => variant.available && JSON.stringify(variant.options) === JSON.stringify(footwearOptions));
    }

    /**
     * Find the live footwear product JSON by handle, since the copy saved in sessionStorage can have stale stock.
     * @param {string} handle footwear product handle
     * @returns {?Object} the rendered product JSON, or null when it is not on the page
     */
    function getLiveFootwearProduct(handle) {
        const footwearProducts = Array.from(section.querySelectorAll(".productFormOptionSlideClogsItemValuesLabelInput.type")).map(input => JSON.parse(input.value));
        return footwearProducts.find(footwearProduct => footwearProduct.handle === handle) ?? null;
    }

    /**
     * Disable footwear sizes on the size slide that are out of stock for the chosen footwear, color and gender,
     * and clear the chosen size if it just became disabled.
     */
    function refreshFootwearSizes() {
        const typeInput = section.querySelector(".productFormOptionSlideClogsItemValuesLabelInput.type:checked");
        const color = section.querySelector(".productFormOptionSlideClogsItemValuesLabelInput.color:checked")?.value;
        const gender = section.querySelector(".productFormOptionSlideClogsItemGenderLabelInput:checked")?.value;
        if (!typeInput || !color || !gender) {
            return;
        }

        // a disabled radio can't be picked from its label, so out of stock sizes become unselectable
        const footwearProduct = JSON.parse(typeInput.value);
        section.querySelectorAll(".productFormOptionSlideSizeLabelInput").forEach(input => {
            input.disabled = !isFootwearAvailable(footwearProduct, [color, gender, input.value]);
            if (input.disabled) {
                input.checked = false;
            }
        });
    }

    /**
     * Disable footwear size options in the pair modal that are out of stock for the chosen footwear, color and gender.
     * Invalid chosen sizes are reset by refreshPairModalDisabledOptions.
     * @param {Element} pairElement the .productFormForm containing the modal to refresh
     */
    function refreshPairModalFootwearSizes(pairElement) {
        const typeSelect = pairElement.querySelector(".productFormPairModalLabelSelect.clogType");
        const colorSelect = pairElement.querySelector(".productFormPairModalLabelSelect.clogColor");
        const genderSelect = pairElement.querySelector(".productFormPairModalLabelSelect.clogGender");
        const sizeOptions = pairElement.querySelectorAll(".productFormPairModalLabelSelect.clogSize option");

        // until footwear, color and gender are all answered, every size stays open
        let footwearProduct = null;
        if (typeSelect.selectedIndex > 0 && colorSelect.selectedIndex > 0 && genderSelect.selectedIndex > 0) {
            footwearProduct = JSON.parse(typeSelect.value);
        }

        sizeOptions.forEach((option, index) => {
            // the "Choose Size" placeholder stays disabled
            if (index === 0) {
                return;
            }
            if (!footwearProduct) {
                option.disabled = false;
                return;
            }
            option.disabled = !isFootwearAvailable(footwearProduct, [colorSelect.value, genderSelect.value, option.value]);
        });
    }

    // initial pass so globally dead choices (e.g. sizes no product carries) are grayed from the start
    refreshDisabledOptions();


    // get orders sold
    fetch("https://cdn.vktry.com/order-count.json")
        .then(response => response.json())
        .then(response => {
            // show badge only if there are orders
            if (response.gold_order_count && response.gold_order_count > 0) {
                // format number with commas
                const ordersSoldCommas = response.gold_order_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                section.querySelector("#productSoldTxt").innerHTML = `${ordersSoldCommas} Sold <br> In Last 7 Days`;
                section.querySelector("#productSold").classList.add("active");
            }
        })


    // set up images swiper
    //INIT THUMBNAILS FIRST
    const thumbSwiper = new Swiper(section.querySelector("#productSwiperThumbs"), {
        spaceBetween: 6,
        slidesPerView: 5,
        freeMode: true,
        watchSlidesProgress: true,
    });
    // INIT MAIN SWIPER
    const productSwiper = new Swiper(section.querySelector("#productSwiper"), {
        slidesPerView: 1,
        spaceBetween: 50,
        thumbs: {
            swiper: thumbSwiper,
        },
        navigation: {
            nextEl: section.querySelector(".productSwiperNext"),
            prevEl: section.querySelector(".productSwiperPrev"),
        },
        loop: false,
        mousewheel: {
            forceToAxis: true
        }
    });

    // Patrol: Swiper's navigation sets native `disabled` on the edge button and its
    // a11y module adds aria-disabled on top; the native state alone is announced.
    const patrolDropRedundantAriaDisabled = () => {
        section.querySelectorAll(".productSwiperPrev, .productSwiperNext").forEach((button) => {
            if (button.disabled) button.removeAttribute("aria-disabled");
        });
    };
    productSwiper.on("afterInit toEdge fromEdge lock unlock", patrolDropRedundantAriaDisabled);
    patrolDropRedundantAriaDisabled();

    // change slide on thumbnail click
    section.querySelectorAll(".productThumbsItem").forEach(element => {
        element.addEventListener("click", function () {
            // change slide
            productSwiper.slideTo(this.dataset.index - 1);
            refreshDisabledOptions();
        });
    });


    // update active variant
    let formData = {
        items: []
    };

    // function to take variables from product options to a formdata item
    function getFormData(options, targetHandle) {
        // gender is a variant (make sure something is checked)
        let gender = options.gender ?? null;

        // sport is a property
        let sport = options.sport ?? null;

        // size is a variant
        let size = options.size ?? null;

        // weight is a property
        let weight = options.weight ?? null;

        // age is a property
        let age = options.age ?? null;

        // Determine product
        let targetProduct = goldWeightToProduct[weight];


        // Determine Top Cover
        let topCover = sportToTopCover[sport];

        // overrides
        // AFTER determining the product (based on the weight) we check on special cases
        // Note we do not touch the weight variable since it's used as a line item property
        // if they are Female and size 151-180, it changes the weight to pro level 3 from level 4 (we have another case for ages 50 and up so we exclude that)
        if (gender === "Female" && weight === "151 - 180lbs" && age !== "50 and Up") {
            targetProduct = goldWeightToProduct["121 - 150lbs"];
        }

        // if they are age 50 & up, we decrease the pro level by 1
        // if they are age 50 & up, we decrease the pro level by 1
        if (age === "50 and Up") {
            if (weight === "261lbs - Up") {
                targetProduct = goldWeightToProduct["231 - 260lbs"];
            } else if (weight === "231 - 260lbs" || weight === "211 - 230lbs") {
                targetProduct = goldWeightToProduct["181 - 210lbs"];
            } else if (weight === "181 - 210lbs" || weight === "151 - 180lbs") {
                targetProduct = goldWeightToProduct["121 - 150lbs"];
            } else if (weight === "121 - 150lbs") {
                targetProduct = goldWeightToProduct["100 - 120lbs"];
            } else {
                targetProduct = goldWeightToProduct["100 - 120lbs"];
            }
        }


        // find the correct variant based on the active options
        let activeVariant = null;
        for (let i = 0; i < targetProduct.variants.length; i++) {

            // here comes the tricky part, Shopfiy might have 5-5.5 OR 5.5-6 ending on the top cover however we're going to receive a size of 5, 5.5 or 6.
            // We need to do a split and find an exact match
            if (
                // size
                targetProduct.variants[i].option1.split("-").includes(size) &&
                // top cover
                targetProduct.variants[i].option2 === topCover &&
                // gender
                targetProduct.variants[i].option3 === gender
            ) {
                activeVariant = targetProduct.variants[i];
                break;
            }
        }

        // check if active variant is not found in any setup
        if (activeVariant == null) {
            return false;
        }

        // gather item
        let formData = {
            quantity: 1,
            id: activeVariant.id,
            properties: {
                "Age": age,
                "Sport": sport,
                "Weight": weight
            },
            available: activeVariant.available
        };

        // return
        return formData;

    }



    /**************
     Product Slider
     ***************/

        // enable swiper
    let productFormSwiper = new Swiper(".productFormSwiper", {
            allowTouchMove: false,
            autoHeight: true,
            spaceBetween: 30
        });


    // Progress bar
    productFormSwiper.on("slideChange", function () {
        // enable all buttons
        section.querySelectorAll('.productFormProgressCircleInner').forEach((element) => {
            element.disabled = false;
        });


        // update active progress bar circles
        section.querySelectorAll(".productFormProgressCircle")?.forEach((element, index) => {
            if (index <= productFormSwiper.activeIndex) {
                // we need to wait to add the active class
                // because the progress bar takes 300ms to animate
                setTimeout(() => {
                    element.classList.add("active");
                }, 100);
            } else {
                element.classList.remove("active");
            }
        });

        // update active progress bar lines
        section.querySelectorAll(".productFormProgressLineInner").forEach((element, index) => {
            element.classList.remove("active");
            if (index < productFormSwiper.activeIndex) {
                element.classList.add("active");
            }
        });

        // Progress bar button click slide change
        section.querySelectorAll('.productFormProgressCircle.active').forEach((element, index) => {
            element.addEventListener("click", function () {
                productFormSwiper.slideTo(index);
            });
        });

    });


    // Progress bar final slide edge cases
    // The final form is the last "slide" but isn't a swiper slide.
    // We need to add the active class to the final progress bar/circle when
    // the last swiper slide option is clicked, which is the weight group for platinum
    // and is the age group for silver/gold.
    let targetGroup = "age"


    section.querySelectorAll(`.productFormOptionSlideLabel[data-group='${targetGroup}']`)?.forEach(element => {
        element.addEventListener("click", function () {
            if (this.classList.contains("soldOut")) {
                return;
            }
            // add active class to all progress bar circles
            section.querySelectorAll(".productFormProgressCircle")?.forEach((element, index) => {
                element.classList.add("active");
            });
            // add active class to all progress bar lines
            section.querySelectorAll(".productFormProgressLineInner")?.forEach((element, index) => {
                element.classList.add("active");
            });

            // Since we're on the last slide, we need to disable the buttons
            section.querySelectorAll('.productFormProgressCircleInner').forEach((element) => {
                element.disabled = true;
            });
        });
    });


    // update active variant on click
    section.querySelectorAll(".productFormOptionSlideLabel").forEach(function (element) {
        element.addEventListener("click", function () {
            // ignore choices that lead only to variants we do not carry
            if (this.classList.contains("soldOut")) {
                return;
            }


            const group = this.dataset.group;
            const value = this.dataset.value;

            // remove active class from all items in this group
            section.querySelectorAll(`.productFormOptionSlideLabel[data-group="${group}"]`).forEach(element => element.classList.remove("active"));

            // add active class to this item
            this.classList.add("active");

            // recompute which upcoming choices are dead ends for the new selection
            refreshDisabledOptions();

            // go to next slide
            productFormSwiper.slideNext();


            // check if every group has an active item
            if (section.querySelectorAll(".productFormOptionSlide").length === section.querySelectorAll(".productFormOptionSlideLabel.active").length) {
                section.querySelector(".productFormWrap").classList.remove("active");
                section.querySelector(".productFormSwiper").classList.remove("active");
                section.querySelector(".productFormForm").classList.add("active");
            } else {
                section.querySelector(".productFormWrap").classList.add("active");
                section.querySelector(".productFormSwiper").classList.add("active");
                section.querySelector(".productFormForm").classList.remove("active");
                return;
            }

            // create options object
            let options = {};
            section.querySelectorAll(`.productFormOptionSlideLabel.active`).forEach(element => {
                options[element.dataset.group] = element.dataset.value;
            });

            // save options
            section.querySelector(".productFormReceipt").setAttribute("options", JSON.stringify(options));


            // update product
            updateProduct();
        });
    });

    // Disable colors that are not available for the selected clog or slide product
    section.querySelectorAll(".productFormOptionSlideClogsItemValuesLabelInput.type").forEach(function (input) {
        input.addEventListener("click", function () {
            // Show the selected type color selector
            if (input.checked) {
                section.querySelectorAll(".productFormOptionSlideClogsItemValues.color").forEach((element) => {
                    element.classList.remove("active");
                })
                section.querySelector(`[id="${input.dataset.colorTarget}"]`).classList.add("active");
                section.querySelector(`[id="${input.dataset.colorTarget}"] .productFormOptionSlideClogsItemValuesLabelInput.color`).click();
            }


            // Just for reference. The thumbnail images for the footwear products
            let iconMap = {
                Clogs: {
                    "Carbon Black": "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/image_3_339c99fe-96d3-4243-ae96-9360c0497e76.png?v=1760397014&width=500",
                    "Cloud White": "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/Render.bip.1681_d2c3befd-4cef-4a7d-b217-e3af07d0151f.png?v=1764094893&width=500",
                    "Fresh Navy": "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/exp_02.2486.png?v=1783373837"
                },
                Slides: {
                    "Carbon Black": "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/2025_C3_Render_recovery_CLOG.1667_2.png?v=1760397119&width=500",
                    "Cloud White": "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/2025_C3_Render_recovery_CLOG.1666_25369c37-7037-40e7-9b11-e205618c3330.png?v=1764094893&width=500",
                    "Fresh Navy": ""
                }
            };
        });
    });

    // Clog/Slide title change
    section.querySelectorAll('.productFormOptionSlideClogsItemValuesLabelInput.type').forEach(function (input) {
        input.addEventListener("click", function () {
            section.querySelector('.productFormOptionSlideClogsItemControlsTitleVal.type').textContent = this.dataset.title;
        })
    })
    // CLog/Slide color title change
    section.querySelectorAll('.productFormOptionSlideClogsItemValuesLabelInput.color').forEach(function (input) {
        input.addEventListener("click", function () {
            section.querySelector('.productFormOptionSlideClogsItemControlsTitleVal.color').textContent = this.dataset.title;
        })
    })

    // re-check footwear sizes whenever the footwear, color or gender changes
    section.addEventListener("change", function (e) {
        if (!e.target.matches(".productFormOptionSlideClogsItemValuesLabelInput, .productFormOptionSlideClogsItemGenderLabelInput")) {
            return;
        }
        refreshFootwearSizes();
    });

    // autoselect white clogs on load
    section.querySelector(`.productFormOptionSlideClogsItemValuesLabelInput[value='Cloud White']`)?.click();
    refreshFootwearSizes();


    // Last slide is clogs size, show the final slide after they click
    section.querySelectorAll(`.productFormOptionSlideSizeLabelInput`)?.forEach(input => {
        input.addEventListener("change", function () {
            section.querySelector(".productFormWrap").classList.remove("active");
            section.querySelector(".productFormSwiper").classList.remove("active");
            section.querySelector(".productFormForm").classList.add("active");

            // create options object
            let options = {};
            let optionsToStore = {}
            section.querySelectorAll(`.productFormOptionSlideLabel.active`).forEach(element => {
                options[element.dataset.group] = element.dataset.value;
                optionsToStore[element.dataset.group] = element.dataset.value;
            });

            // Save the clogs/slides settings too
            let footwear = JSON.parse(section.querySelector('.productFormOptionSlideClogsItemValuesLabelInput.type:checked').value);
            optionsToStore["footwearProduct"] = footwear;

            let color = section.querySelector('.productFormOptionSlideClogsItemValuesLabelInput.color:checked').value;
            optionsToStore["footwearColor"] = color;

            let size = section.querySelector('.productFormOptionSlideSizeLabelInput:checked').value;
            optionsToStore["footwearSize"] = size;

            // footwear gender is now its own choice (falls back to insole gender when the selector is hidden)
            let gender = section.querySelector('.productFormOptionSlideClogsItemGenderLabelInput:checked')?.value ?? options.gender;
            optionsToStore["footwearGender"] = gender;

            let footwearType = section.querySelector('.productFormOptionSlideClogsItemValuesLabelInput.type:checked').dataset.title;
            optionsToStore["footwearType"] = footwearType;

            // save options
            section.querySelector(".productFormReceipt").setAttribute("options", JSON.stringify(options));
            sessionStorage.setItem("options", JSON.stringify(optionsToStore));

            // update product
            updateProduct();

        })
    });

    // back button
    section.querySelectorAll(".productFormSwiperNavBack").forEach(function (element) {
        element.addEventListener("click", function () {
            productFormSwiper.slidePrev();
        });
    });

    // next button
    section.querySelectorAll(".productFormSwiperNavNext").forEach(function (element) {
        element.addEventListener("click", function () {
            productFormSwiper.slideNext();
        });
    });


    // trigger close modal
    section.querySelectorAll(".productFormPairModal,.productFormPairModalCancel,.productFormPairModalExit").forEach(element => {
        element.addEventListener("click", function (e) {
            if (e.target === this) {
                section.querySelector(".productFormPairModal[open]").close();
            }
        })
    });

    // form input events
    section.querySelectorAll(".productFormPairModalLabelSelect").forEach(element => element.addEventListener("change", function () {
        // recompute dead ends for the new answer and reset selects that just became invalid
        refreshPairModalDisabledOptions(this.closest(".productFormForm"));

        // check if the entire form is ready
        let form = this.closest(".productFormPairModalContent");

        // check if all inputs are entered
        let ready = true;
        form.querySelectorAll(".productFormPairModalLabelSelect").forEach(input => {
            if (input.selectedIndex === 0) {
                ready = false;
            }
        });

        // update update button
        form.querySelector(".productFormPairModalUpdate").disabled = !ready;
    }));

    // update pair
    section.querySelectorAll(".productFormPairModalContent").forEach(element => {
        element.addEventListener("submit", function (e) {
            e.preventDefault();

            const storedOptions = JSON.parse(sessionStorage.getItem("options"));

            // save pair element
            let pairElement = section.querySelector(".productFormReceipt");

            // update options attribute only if the option isn't the initial one
            let insoleOptions = {};
            section.querySelector(".productFormPairModal").querySelectorAll(`.productFormPairModalLabelSelect.insole`).forEach(element => {
                if (!element.value.includes("Choose")) {
                    insoleOptions[element.getAttribute("name")] = element.value;
                    storedOptions[element.getAttribute("name")] = element.value;
                }
            });

            pairElement.setAttribute("options", JSON.stringify(insoleOptions));

            // update clog options
            let editModal = section.querySelector('.productFormPairModal');

            let color = editModal.querySelector('.productFormPairModalLabelSelect.clogColor').value;
            storedOptions.footwearColor = color;

            let type = editModal.querySelector('.productFormPairModalLabelSelect.clogType').value;
            storedOptions.footwearProduct = JSON.parse(type);

            let size = editModal.querySelector('.productFormPairModalLabelSelect.clogSize').value;
            storedOptions.footwearSize = size;

            let genderClog = editModal.querySelector('.productFormPairModalLabelSelect.clogGender').value;
            storedOptions.footwearGender = genderClog;

            section.querySelectorAll('.productFormOptionSlideClogsItemGenderLabelInput').forEach(element => {
                if (element.value === genderClog) {
                    element.checked = true
                }
            })
            section.querySelectorAll('.productFormOptionSlideClogsItemValuesLabelInput.color').forEach(element => {
                if (element.value === color) {
                    element.checked = true
                }
            })
            section.querySelectorAll('.productFormOptionSlideClogsItemValuesLabelInput.type').forEach(element => {
                if (element.value === type) {
                    element.checked = true
                }
            })
            section.querySelectorAll('.productFormOptionSlideSizeLabelInput').forEach(element => {
                if (element.value === size) {
                    element.checked = true
                }
            })

            // Save the updated ticket
            sessionStorage.setItem("options", JSON.stringify(storedOptions));

            // close modal
            section.querySelector(".productFormPairModal[open]").close();

            updateProduct();
        });
    });

    // open edit
    section.querySelectorAll(".productFormActionEdit").forEach(element => {
        element.addEventListener("click", function () {
            // save pair element
            let pairElement = section.querySelector(".productFormForm");
            const pairReceipt = section.querySelector(".productFormReceipt");
            let editModal = section.querySelector('.productFormPairModal');
            const storedOptions = JSON.parse(sessionStorage.getItem("options"));
            console.log("StoredOptions: ", storedOptions);

            // open modal
            section.querySelector(".productFormPairModal").showModal();

            // select insole options
            editModal.querySelectorAll(`.productFormPairModalLabelSelect`).forEach(element => {
                element.value = storedOptions[element.getAttribute("name")]
                //console.log(element.value, optionsParsed[element.getAttribute("name")]);
            });

            // select clog options
            editModal.querySelector('.productFormPairModalLabelSelect.clogGender').value = storedOptions.footwearGender;
            editModal.querySelector('.productFormPairModalLabelSelect.clogColor').value = storedOptions.footwearColor;
            // The value assigned to the option in the edit modal is a JSON escaped product json, which does not match what is
            // stored for the session, so we are going assign the pre-selected value based on the product handle
            section.querySelectorAll(".productFormPairModalLabelSelect.clogType option").forEach(option => {
                if (option.dataset.handle === storedOptions.footwearProduct.handle) {
                    option.selected = true;
                }
            });
            editModal.querySelector('.productFormPairModalLabelSelect.clogSize').value = storedOptions.footwearSize;

            // gray out choices that are dead ends for this pair's current answers
            refreshPairModalDisabledOptions(pairElement);

        });
    });

    // remove
    section.querySelectorAll(".productFormActionRemove").forEach(element => {
        element.addEventListener("click", function () {
            // save pair element
            let pairElement = section.querySelector(".productFormReceipt");

            // remove active and options
            pairElement.removeAttribute("options");

            section.querySelector(".productFormSwiper").classList.add("active");
            section.querySelector(".productFormForm").classList.remove("active");
            section.querySelector(".productFormWrap").classList.add("active");
            section.querySelectorAll(".productFormOptionSlideLabel.active").forEach(element => element.classList.remove("active"));

            // uncheck footwear size
            if (section.querySelector('.productFormOptionSlideSizeLabelInput:checked')) {
                section.querySelector('.productFormOptionSlideSizeLabelInput:checked').checked = false;
            }

            // with all answers cleared, recompute so only globally dead choices stay grayed
            refreshDisabledOptions();

            productFormSwiper.slideTo(0);

        });
    });

    // "Start a New Bundle" button listener
    section.querySelectorAll(".productFormErrorButton").forEach(button => {
        button.addEventListener("click", function () {
            // save pair element
            let pairElement = section.querySelector(".productFormReceipt");

            // remove active and options
            pairElement.removeAttribute("options");

            section.querySelector(".productFormSwiper").classList.add("active");
            section.querySelector(".productFormForm").classList.remove("active");
            section.querySelector(".productFormWrap").classList.add("active");
            section.querySelectorAll(".productFormOptionSlideLabel.active").forEach(element => element.classList.remove("active"));

            // uncheck footwear size
            if (section.querySelector('.productFormOptionSlideSizeLabelInput:checked')) {
                section.querySelector('.productFormOptionSlideSizeLabelInput:checked').checked = false;
            }
            // with all answers cleared, recompute so only globally dead choices stay grayed
            refreshDisabledOptions();

            productFormSwiper.slideTo(0);

        });
    })


    async function updateProduct() {
        // reset atc button (atc active)
        section.querySelector(".productFormATC").textContent = section.querySelector(".productFormATC").dataset.atcText;
        section.querySelector(".productFormATC").classList.add("active");
        section.querySelector(".productFormATC").classList.remove("disabled");
        section.querySelector(".productFormATC").classList.remove("message");
        section.querySelector(".productFormFormTitle").classList.remove("active");
        section.querySelector(".productFormReceiptWrapper").classList.remove("active");

        // show terms
        // section.querySelector(".productFormTerms").classList.remove("active");

        // reset form data
        formData.items = [];

        // hide error message
        section.querySelector(".productFormATC").classList.remove("active");


        //  find the insoles product
        let options = JSON.parse(section.querySelector('.productFormReceipt').getAttribute("options"));
        let insoles = getFormData(options, product.handle);

        console.log("insoles: ", insoles);
        console.log("options: ", options);

        // find the clogs/slides product (footwear)
        let footwear = JSON.parse(section.querySelector('.productFormOptionSlideClogsItemValuesLabelInput.type:checked').value)
        let color = section.querySelector('.productFormOptionSlideClogsItemValuesLabelInput.color:checked').value;
        let size = section.querySelector('.productFormOptionSlideSizeLabelInput:checked').value;
        // footwear gender is now its own choice (falls back to insole gender when the selector is hidden)
        let gender = section.querySelector('.productFormOptionSlideClogsItemGenderLabelInput:checked')?.value ?? options.gender;
        let footwearType = section.querySelector('.productFormOptionSlideClogsItemValuesLabelInput.type:checked').dataset.title;

        // Use the options to find the correct variant for the clogs/slides
        let clogVariant = null;
        let clogsOptions = [color, gender, size];
        clogVariant = footwear.variants.find((variant) => {
            return JSON.stringify(variant.options) === JSON.stringify(clogsOptions)
        });

        // Adjust the copy depending on the footwear type
        if (footwearType.includes("Slides")) {
            footwearType = "Slides";
        } else if (footwearType.includes("Clog")) {
            footwearType = "Clogs";
        }

        // check if insoles/footwear is not found (variant can't be found)
        if (insoles === false || !insoles.available || !clogVariant || !clogVariant.available) {
            // hide regular title
            section.querySelector(".productFormFormTitle").classList.add("active");

            // hide to receipt wrapper
            section.querySelector(".productFormReceiptWrapper").classList.add("active");

            // hide help call out
            section.querySelector(".productFormHelp").classList.add("active");

            // hide atc button
            section.querySelector(".productFormATC").classList.add("active");

            // hide terms
            section.querySelector(".productFormShipping").classList.add("active");

            // hide terms
            //section.querySelector(".productFormTerms").classList.add("active");

            let errorMsg = "Sorry, we don't offer a product that meets the selected options.";


            if (!insoles.available) {
                errorMsg = "Sorry, this product is currently out of stock.";
            }

            if (insoles === false) {
                errorMsg = "Unavailable — this selection is not currently part of our assortment";
            }


            //section.querySelector('.productFormErrorMsg').textContent = errorMsg;

            // show error message
            section.querySelector(".productFormError").classList.add("active");

            // log request
            let formData = new FormData();
            formData.set("handle", product.handle);
            formData.set("options", JSON.stringify(options));
            fetch("https://api.vktry.com/variant-not-found.php", {
                method: "POST",
                body: formData
            });

            return;
        }


        // build the receipt
        let receiptElement = section.querySelector(".productFormReceipt");
        receiptElement.innerHTML = ``;

        // Insoles Data
        let insoleHTML = `
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle bold">Gold Insoles</div>
                <div class="productFormReceiptRowValue"></div>
            </div>`

        for (const [key, value] of Object.entries(options)) {
            insoleHTML += `
                <div class="productFormReceiptRow">
                    <div class="productFormReceiptRowTitle">${key}:</div>
                    <div class="productFormReceiptRowValue">${value}</div>
                </div>
            `
        }

        insoleHTML += `
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">${footwearType} Color:</div>
                <div class="productFormReceiptRowValue">${color}</div>
            </div>
        `;

        // Clogs/Slides Data
        insoleHTML += `
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle bold">${footwearType} in ${color}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Gender:</div>
                <div class="productFormReceiptRowValue">${gender}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Footwear Size:</div>
                <div class="productFormReceiptRowValue">${size}</div>
            </div>`
        // insoleHTML += `<div class="productFormReceiptLine"></div>`



        // Extras

        insoleHTML += `
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle bold">Plus</div>
                <div class="productFormReceiptRowValue"></div>
            </div>
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle">Drawstring Bag, Grip Socks & Footwear Cleaner</div>
               
            </div>
<!--            <div class="productFormReceiptRow">-->
<!--                <div class="productFormReceiptRowTitle">Drawstring Bag:</div>-->
<!--                <div class="productFormReceiptRowValue">Included</div>-->
<!--            </div>-->
<!--            <div class="productFormReceiptRow">-->
<!--                <div class="productFormReceiptRowTitle">Footwear Cleaner:</div>-->
<!--                <div class="productFormReceiptRowValue">Included</div>-->
<!--            </div>-->
<!--            <div class="productFormReceiptRow">-->
<!--                <div class="productFormReceiptRowTitle">Grip Socks:</div>-->
<!--                <div class="productFormReceiptRowValue">Included</div>-->
<!--            </div>-->
`


        receiptElement.innerHTML = insoleHTML;


        // show atc
        section.querySelector(".productFormATC").classList.remove("active");

        // show terms
        // section.querySelector(".productFormTerms").classList.remove("active");

        // hide error message
        section.querySelector(".productFormError")?.classList.remove("active");

        // check for duplicates (id and property, if you check the entire object, the id won't match)
        for (let i = 0; i < formData.items.length; i++) {
            if (formData.items[i].id === item.id && JSON.stringify(formData.items[i].properties) === JSON.stringify(item.properties)) {
                formData.items[i].quantity++;
                return;
            }
        }

        // add a hidden property to identify the kit as a group
        let groupId = Date.now();

        // get size of insoles for grip socks
        let insolesSize = options.size;
        let insolesGender = options.gender

        // grip socks
        let gripSocksVariantID
        if ((insolesGender === "Male" && isSizeInRange(insolesSize, 0, 7)) || (insolesGender === "Female" && isSizeInRange(insolesSize, 0, 9))) {
            gripSocksVariantID = 51654863061359 // medium
        } else if ((insolesGender === "Male" && isSizeInRange(insolesSize, 0, 12)) || (insolesGender === "Female" && isSizeInRange(insolesSize, 0, 14))) {
            gripSocksVariantID = 51654863094127; // large
        } else {
            gripSocksVariantID = 51654863126895; // xlarge
        }

        // check grip socks availability
        let gripSocksVariant = gripSocksProduct.variants.find(v => v.id === gripSocksVariantID);
        if (!gripSocksVariant || !gripSocksVariant.available) {
            section.querySelector(".productFormATC").classList.add("active");
            section.querySelector('.productFormErrorMsg').textContent = "Sorry, this product is currently out of stock.";
            section.querySelector(".productFormError").classList.add("active");
            return;
        }


        function isSizeInRange(currentSize, min, max) {
            const range = currentSize.split('-').map(parseFloat);
            const size = parseFloat(range[0]);
            return !isNaN(size) && size >= min && size <= max;
        }

        formData.items.push({
            quantity: 1,
            id: 52711431864687, // Luxury Box
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        formData.items.push({
            quantity: 1,
            id: 52392856060271, // cleaner sponge
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        // add extras (drawstring bag and shoe cleaner)
        formData.items.push({
            quantity: 1,
            id: 51675503427951, // drawstring bag
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        formData.items.push({
            quantity: 1,
            id: gripSocksVariantID, // Grip Socks
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        // add clogs
        formData.items.push({
            quantity: 1,
            id: clogVariant.id, // Footwear
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        // Add Insoles
        // add discount property to insoles
        insoles.properties._discount_kit = "luxury box v3";
        formData.items.push(insoles);
    }

    // dynamic shoe size title
    section.querySelectorAll(".productFormOptionSlideLabel[data-group='gender']").forEach(element => {
        element.addEventListener("click", function () {
            //pass when option is not available
            if (this.classList.contains("soldOut")) {
                return;
            }
            section.querySelector('.productFormOptionSlideTitleGender').textContent = element.textContent;

            // default the footwear gender to match the chosen insole gender (user can still override on the clogs slide)
            let footwearGenderInput = section.querySelector(`.productFormOptionSlideClogsItemGenderLabelInput[value="${element.dataset.value}"]`);
            if (footwearGenderInput) {
                footwearGenderInput.checked = true;
            }

            // setting checked doesn't fire change, so re-check footwear sizes for the new gender here
            refreshFootwearSizes();
        })
    })


    /**************
     Product ATC
     ***************/

    section.querySelector(".productFormATC").addEventListener("click", async function () {
        // add loading animation
        this.classList.add("loading");

        // first fetch the cart and check if any items in the cart have a property of _group_id,
        // if they do, we need to generate a new group id for the new kit to avoid merging them together
        let maxGroupID = 0;
        let cart = await getCart();
        cart.items.forEach(item => {
            if (item.properties && item.properties._group_id) {
                let itemGroupID = parseInt(item.properties._group_id);
                if (itemGroupID > maxGroupID) {
                    maxGroupID = itemGroupID;
                }
            }
        });
        let groupID = maxGroupID + 1;

        // update all items in formData to have the same group id
        formData.items.forEach(item => {
            item.properties._group_id = groupID;
        });


        // add product
        await addProduct(formData);

        // stop loading
        this.classList.remove("loading");

        // open cart
        await openCart();
    });

    /********************************** Shoe Size **********************************/


    // open shoe size modal
    document.querySelectorAll(".openShoeSize").forEach(element => {
        element.addEventListener("click", function () {
            // Find the correct shoe size modal

            // The modal to open is based on the sport
            // Gold: Basketball, fitness, racquet sports, running, volleyball, walking/hiking, everyday
            // Black: Baseball, boxing, football, golf, lacrosse, rugby, ultimate frisbee, [NEW -> ]Cheerleading, Track, Soccer
            // Red: REMOVED and uses black now, was Cheerleading, Track, Soccer
            let sport = document.querySelector(`.productFormOptionSlideLabel.active[data-group="sport"]`)?.dataset?.value;

            if (sport === "Basketball" || sport === "Fitness" || sport === "Tennis/Pickleball" || sport === "Running" || sport === "Volleyball" || sport === "Hiking" || sport === "Walking" || sport === "Other") {
                document.querySelector('#shoeSizeGold').showModal();
            } else {
                document.querySelector('#shoeSizeBlack').showModal();
            }

            document.querySelector("body").classList.add("noScroll");

        })
    });

    // open clog size modal
    document.querySelectorAll(".upsellSize")?.forEach(element => {
        element.addEventListener("click", function () {
            document.querySelector('#shoeSizeClog').showModal();
            document.querySelector("body").classList.add("noScroll");
        })
    });

    //close shoe size event listener
    document.querySelectorAll(".shoeSizeContentClose")?.forEach(function (element) {
        element.addEventListener("click", async function () {
            let target = this.dataset.target;
            await closeSize(target);

        });
    });

    //close shoe size event listener
    document.querySelectorAll(".shoeSize")?.forEach(function (element) {
        element.addEventListener("click", async function (e) {
            if (e.target === this) {
                let target = element.id;
                await closeSize(target);

            }
        });
    });

    // close shoe size
    function closeSize(target) {
        document.querySelector(`#${target}`).close();
        document.querySelector("body").classList.remove("noScroll");
    }

    // register dialogs
    // wait for document to load first
    document.addEventListener("DOMContentLoaded", function () {
        document.querySelectorAll("dialog.shoeSize")?.forEach(function (element) {
            dialogPolyfill.registerDialog(element);
        });
    });

    // Listener to disable unavailable colors in the edit modal
    const footwearTypeModalInput = section.querySelector(".productFormPairModalLabelSelect.clogType");
    footwearTypeModalInput.addEventListener("change", () => {
        const footwearOptions = JSON.parse(footwearTypeModalInput.querySelector("option:checked").dataset.productOptions);
        const availableColors = footwearOptions.find((option) => {
            return option ? option["name"] === "Color" : false;
        })

        // re-enable all options
        section.querySelectorAll(".productFormPairModalLabelSelect.clogColor option").forEach(option => {
                option.disabled = false;
        });

        // Disable certain unavailable options
        section.querySelectorAll(".productFormPairModalLabelSelect.clogColor option").forEach(option => {
            if (!availableColors.values.includes(option.value)) {
                option.disabled = true;
            }
        });

        // Auto select the first not-disabled option
        const optionsArray = Array.from(section.querySelectorAll(".productFormPairModalLabelSelect.clogColor option"));
        const firstAvailableOption = optionsArray.find((option) => {
            return option ? option.disabled === false : null
        });
        section.querySelector(".productFormPairModalLabelSelect.clogColor").value = firstAvailableOption.value;

        // the color changed after the shared change handler ran, so re-check footwear sizes now
        refreshPairModalDisabledOptions(section.querySelector(".productFormForm"));
    })

    /**************
     Money Back
     ***************/

    // open moneyBack dialog
    document.querySelectorAll('.openMoneyBack').forEach(function (element) {
        element.addEventListener('click', function () {
            document.querySelector("#moneyBack").showModal();
            document.querySelector("body").classList.add("noScroll");
        });
    })

    // close moneyBack
    function closePopup() {
        document.querySelector("#moneyBack").close();
        document.querySelector("body").classList.remove("noScroll");
    }

    // close moneyBack event listener
    document.querySelectorAll(".moneyBackClose").forEach(function (element) {
        element.addEventListener("click", closePopup);
    });

    document.querySelector("#moneyBack").addEventListener("click", function (e) {
        if (e.target === this) {
            closePopup();
        }
    })

    // close event
    document.querySelector("#moneyBack").addEventListener("close", function () {
        document.querySelector("body").classList.remove("noScroll");
    });

    // register dialog
    dialogPolyfill.registerDialog(document.querySelector("#moneyBack"));

    // setup Dropdowns
    section.querySelectorAll(".productInfoDropTop").forEach(element => {
        element.addEventListener("click", function () {
            this.classList.toggle("active");
        });
    });

    // Setup email OOS input listener
    const oosForm = section.querySelector("#boxOosEmailSignup");
    oosForm.addEventListener("submit", async function (e) {
        e.preventDefault();

        // Grab our email value
        const email = oosForm.querySelector("#giftBoxOosEmailSignup").value;
        console.log(email);

        // identify first so the event lands on that profile instead of an anonymous one
        // await klaviyo.identify({
        //     '$email': email
        // });

        // track the event
        //klaviyo.push(["track", "Gift Box OOS", oosEvent]);
    });

    /**
     * Checks to see if there is a sessionStorage item named 'options'. If there is, then we load the data and skip
     * to the end of the form. If there is not, we proceed as normal. The sessionStoage gets added when the form has
     * been completed once. The function will let the customer pick up where they left off.
     */
    function init() {
        if (!sessionStorage.getItem("options")) {
            return
        }

        // reset form data
        formData.items = [];

        // SETUP OUR VARIABLES
        const storedOptions = JSON.parse(sessionStorage.getItem("options"));
        // INSOLE OPTIONS
        const age = storedOptions.age;
        const gender = storedOptions.gender;
        const weight = storedOptions.weight;
        const sport = storedOptions.sport;
        const insoleSize = storedOptions.size;
        const footwearProduct = storedOptions.footwearProduct;
        const footwearColor = storedOptions.footwearColor;
        const footwearGender = storedOptions.footwearGender;
        const footwearSize = storedOptions.footwearSize;
        const footwearType = storedOptions.footwearType;

        // Get the insoles product
        const insoles = getFormData(storedOptions, product.handle);

        // Get the footwear variant from the live product (the stored copy's stock can be stale) using the footwear gender
        const liveFootwearProduct = getLiveFootwearProduct(footwearProduct.handle);
        const clogsOptions = [footwearColor, footwearGender, footwearSize];
        const clogVariant = liveFootwearProduct?.variants.find(variant => JSON.stringify(variant.options) === JSON.stringify(clogsOptions));

        // the saved bundle can't be bought as is, so drop it and let the customer build a new one
        if (insoles === false || !insoles.available || !clogVariant || !clogVariant.available) {
            sessionStorage.removeItem("options");
            return;
        }

        // Adjust the copy depending on the footwear type
        let footwearText;
        if (footwearType.includes("Slides")) {
            footwearText = "Slides";
        } else if (footwearType.includes("Clog")) {
            footwearText = "Clogs";
        }

        // build the receipt
        const receiptElement = section.querySelector(".productFormReceipt");
        receiptElement.innerHTML = ``;

        // Insoles Data
        let insoleHTML = `
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle bold">Gold Insoles</div>
                <div class="productFormReceiptRowValue"></div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Gender:</div>
                <div class="productFormReceiptRowValue">${gender}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Sport:</div>
                <div class="productFormReceiptRowValue">${sport}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Size:</div>
                <div class="productFormReceiptRowValue">${insoleSize}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Weight:</div>
                <div class="productFormReceiptRowValue">${weight}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Age:</div>
                <div class="productFormReceiptRowValue">${age}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">${footwearText} Color:</div>
                <div class="productFormReceiptRowValue">${footwearColor}</div>
            </div>`;

        // Clogs/Slides Data
        insoleHTML += `
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle bold">${footwearText} in ${footwearColor}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Gender:</div>
                <div class="productFormReceiptRowValue">${footwearGender}</div>
            </div>
            <div class="productFormReceiptRow">
                <div class="productFormReceiptRowTitle">Footwear Size:</div>
                <div class="productFormReceiptRowValue">${footwearSize}</div>
            </div>`;

        // Extras
        insoleHTML += `
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle bold">Plus</div>
                <div class="productFormReceiptRowValue"></div>
            </div>
            <div class="productFormReceiptRowHead">
                <div class="productFormReceiptRowTitle">Drawstring Bag, Grip Socks & Footwear Cleaner</div>
            </div>`;

        receiptElement.innerHTML = insoleHTML;

        //Show the final receipt slide
        section.querySelector(".productFormWrap").classList.remove("active");
        section.querySelector(".productFormSwiper").classList.remove("active");
        section.querySelector(".productFormForm").classList.add("active");

        // reset atc button (atc active)
        section.querySelector(".productFormATC").textContent = section.querySelector(".productFormATC").dataset.atcText;
        section.querySelector(".productFormATC").classList.add("active");
        section.querySelector(".productFormATC").classList.remove("disabled");
        section.querySelector(".productFormATC").classList.remove("message");
        section.querySelector(".productFormFormTitle").classList.remove("active");
        section.querySelector(".productFormReceiptWrapper").classList.remove("active");

        // hide ATC error message
        section.querySelector(".productFormATC").classList.remove("active");

        // show atc
        section.querySelector(".productFormATC").classList.remove("active");

        // hide Form error message
        section.querySelector(".productFormError").classList.remove("active");

        // check for duplicates (id and property, if you check the entire object, the id won't match)
        for (let i = 0; i < formData.items.length; i++) {
            if (formData.items[i].id === item.id && JSON.stringify(formData.items[i].properties) === JSON.stringify(item.properties)) {
                formData.items[i].quantity++;
                return;
            }
        }

        // add a hidden property to identify the kit as a group
        let groupId = Date.now();

        // get size of insoles for grip socks
        let insolesSize = insoleSize;
        let insolesGender = gender

        // grip socks
        let gripSocksVariantID
        if ((insolesGender === "Male" && isSizeInRange(insolesSize, 0, 7)) || (insolesGender === "Female" && isSizeInRange(insolesSize, 0, 9))) {
            gripSocksVariantID = 51654863061359 // medium
        } else if ((insolesGender === "Male" && isSizeInRange(insolesSize, 0, 12)) || (insolesGender === "Female" && isSizeInRange(insolesSize, 0, 14))) {
            gripSocksVariantID = 51654863094127; // large
        } else {
            gripSocksVariantID = 51654863126895; // xlarge
        }

        // check grip socks availability
        let gripSocksVariant = gripSocksProduct.variants.find(v => v.id === gripSocksVariantID);
        if (!gripSocksVariant || !gripSocksVariant.available) {
            section.querySelector(".productFormATC").classList.add("active");
            section.querySelector('.productFormErrorMsg').textContent = "Sorry, this product is currently out of stock.";
            section.querySelector(".productFormError").classList.add("active");
            return;
        }


        function isSizeInRange(currentSize, min, max) {
            const range = currentSize.split('-').map(parseFloat);
            const size = parseFloat(range[0]);
            return !isNaN(size) && size >= min && size <= max;
        }


        formData.items.push({
            quantity: 1,
            id: 52711431864687, // Luxury Box
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        formData.items.push({
            quantity: 1,
            id: 52392856060271, // cleaner sponge
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        // add extras (drawstring bag and shoe cleaner)
        formData.items.push({
            quantity: 1,
            id: 51675503427951, // drawstring bag
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        formData.items.push({
            quantity: 1,
            id: gripSocksVariantID, // Grip Socks
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        // add clogs
        formData.items.push({
            quantity: 1,
            id: clogVariant.id, // Footwear
            properties: {
                _discount_kit: "luxury box v3",
            }
        });

        // Add Insoles
        // add discount property to insoles
        insoles.properties._discount_kit = "luxury box v3";
        formData.items.push(insoles);
    }
    //Call on load to check for data
    window.addEventListener("DOMContentLoaded", init);

}())
